import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Statistics from './pages/Statistics';
import ProductionValidation from './pages/ProductionValidation';
import TopPositionNego from './pages/TopPositionNego';
import PendingTransactions from './pages/PendingTransactions';
import AccountingObjectives from './pages/AccountingObjectives';
import RegularizationRequests from './pages/RegularizationRequests';
import Reporting from './pages/Reporting';
import ReportingLefeuvre from './pages/ReportingLefeuvre';
import Profile from './pages/Profile';
import ReportingAgence from './pages/ReportingAgence';
import Performance from './pages/Performance';
import { useAuth } from './contexts/AuthContext';
import ChatBot from './components/ChatBot';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';
import ViewTransactionModal from './components/validation/ViewTransactionModal';
import { supabase } from './lib/supabase';
import { Transaction } from './types/dashboard';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AdminOrDirectorRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user || (user.role !== 'admin' && user.role !== 'direction')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AdminDirectorOrReadOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user || (user.role !== 'admin' && user.role !== 'direction' && user.role !== 'lecture')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function UserResponsableAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user || (user.role !== 'user' && user.role !== 'responsable' && user.role !== 'admin' && user.role !== 'direction')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Separate component for the app content to use hooks inside AuthProvider
function AppContent() {
  const { user } = useAuth();

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        
        <Route path="/" element={
          <PrivateRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/statistics" element={
          <AdminOrDirectorRoute>
            <Layout>
              <Statistics />
            </Layout>
          </AdminOrDirectorRoute>
        } />

        <Route path="/performance" element={
          <AdminRoute>
            <Layout>
              <Performance />
            </Layout>
          </AdminRoute>
        } />

        <Route path="/settings" element={
          <AdminRoute>
            <Layout>
              <Settings />
            </Layout>
          </AdminRoute>
        } />

        <Route path="/validation" element={
          <AdminRoute>
            <Layout>
              <ProductionValidation />
            </Layout>
          </AdminRoute>
        } />

        <Route path="/top-position" element={
          <AdminDirectorOrReadOnlyRoute>
            <Layout>
              <TopPositionNego />
            </Layout>
          </AdminDirectorOrReadOnlyRoute>
        } />

        <Route path="/accounting-objectives" element={
          <AdminOrDirectorRoute>
            <Layout>
              <AccountingObjectives />
            </Layout>
          </AdminOrDirectorRoute>
        } />

        <Route path="/regul" element={
          <AdminOrDirectorRoute>
            <Layout>
              <RegularizationRequests />
            </Layout>
          </AdminOrDirectorRoute>
        } />

        <Route path="/pending" element={
          <AdminDirectorOrReadOnlyRoute>
            <Layout>
              <PendingTransactions />
            </Layout>
          </AdminDirectorOrReadOnlyRoute>
        } />

        <Route path="/reporting" element={
          <AdminRoute>
            <Layout>
              <Reporting />
            </Layout>
          </AdminRoute>
        } />

        <Route path="/reporting-lefeuvre" element={
          <AdminRoute>
            <Layout>
              <ReportingLefeuvre />
            </Layout>
          </AdminRoute>
        } />

        <Route path="/reporting-agence" element={
          <UserResponsableAdminRoute>
            <Layout>
              <ReportingAgence />
            </Layout>
          </UserResponsableAdminRoute>
        } />

        <Route path="/profile" element={
          <PrivateRoute>
            <Layout>
              <Profile />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/view-transaction/:id" element={
          <PrivateRoute>
            <Layout>
              <ViewTransactionPage />
            </Layout>
          </PrivateRoute>
        } />
      </Routes>
      {user && <ChatBot />}
      <PWAUpdatePrompt />
    </Router>
  );
}

// Composant pour afficher une transaction via URL
function ViewTransactionPage() {
  const { id } = useParams<{ id: string }>();
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadTransaction(id);
    }
  }, [id]);

  const loadTransaction = async (transactionId: string) => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          transaction_agencies (
            agency
          ),
          transaction_negotiators (
            negotiator:negotiators (
              id,
              firstname,
              lastname
            ),
            role,
            percentage
          ),
          transaction_assistants (
            assistant:assistants (
              id,
              firstname,
              lastname
            ),
            role
          ),
          transaction_retrocessions (
            id,
            type,
            amount
          )
        `)
        .eq('id', transactionId)
        .single();

      if (error) throw error;

      const formattedTransaction = {
        ...data,
        agencies: data.transaction_agencies.map(ta => ta.agency),
        negotiators: data.transaction_negotiators?.map(tn => ({
          id: tn.negotiator.id,
          name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
          role: tn.role,
          percentage: tn.percentage
        })) || [],
        assistants: data.transaction_assistants?.map(ta => ({
          id: ta.assistant.id,
          name: `${ta.assistant.firstname} ${ta.assistant.lastname}`,
          role: ta.role
        })) || [],
        retrocessions: data.transaction_retrocessions?.map(tr => ({
          id: tr.id,
          type: tr.type,
          amount: tr.amount
        })) || []
      };

      setTransaction(formattedTransaction);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        Erreur lors du chargement de la transaction : {error}
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="p-4 bg-yellow-50 text-yellow-600 rounded-lg">
        Transaction non trouvée
      </div>
    );
  }

  return (
    <div>
      <ViewTransactionModal
        transaction={transaction}
        isOpen={true}
        onClose={() => window.history.back()}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;